'use client';

import clsx from 'clsx';
import { useSearchParams } from 'next/navigation';
import { FC, useEffect, useRef, useState } from 'react';
import ArticleCard from '~/components/article-card';
import { fetchArticles } from '~/data/strapi/api/collections/articles';
import Article from '~/data/strapi/types/collection-types/article';
import { RegionLocale } from '~/infra/locale';

import ArticleListButton from './button';
import styles from './styles.module.css';

interface Props {
  initArticles: Article[];
  totalArticles: number;
  pageSize: number;
  buttonTitle: string | null;
  locale: RegionLocale;
  isBlogPage?: boolean;
}

const ArticleList: FC<Props> = ({ initArticles, pageSize, buttonTitle, totalArticles, locale, isBlogPage = false }) => {
  const params = useSearchParams();
  const tag = params.get('tag');
  const isAbleToLoadMoreInit = tag ? false : initArticles.length < totalArticles;

  const [articles, setArticles] = useState<Article[]>(!tag ? initArticles : []);
  const isFirstRender = useRef(true);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [isAbleToLoadMore, setIsAbleToLoadMore] = useState<boolean>(isAbleToLoadMoreInit);

  const showButton = isAbleToLoadMore || !isBlogPage;

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (!tag) return;
    }
    const reloadArticles = async () => {
      const { data, meta } = await fetchArticles({ locale, page: 1, pageSize, tagSlug: tag });
      setArticles(data);
      setPage(1);
      setIsAbleToLoadMore(meta.pagination.total !== data.length);
    };

    reloadArticles();
  }, [tag, locale, pageSize]);

  const handleLoadMore = async () => {
    setLoading(true);
    const { data, meta } = await fetchArticles({
      locale,
      page: page + 1,
      pageSize,
      tagSlug: tag,
    });
    setPage((prev) => ++prev);
    setArticles((prev) => {
      const newArticles = [...prev, ...data];
      setIsAbleToLoadMore(meta.pagination.total !== newArticles.length);

      return newArticles;
    });
    setLoading(false);
  };

  return (
    <>
      <ul className={clsx(styles.grid, page > 1 && styles.mobileFull)}>
        {articles.map((article) => (
          <li key={article.id}>
            <ArticleCard article={article} />
          </li>
        ))}
      </ul>
      {buttonTitle && showButton && (
        <ArticleListButton loading={loading} onClick={handleLoadMore} title={buttonTitle} isBlogPage={isBlogPage} />
      )}
    </>
  );
};

export default ArticleList;
